// ─────────────────────────────────────────────────────────────
// ZIFEK — Horaires d'ouverture d'un restaurant
// Stockés dans `merchant_home_config.opening_hours` (jsonb) sous la
// forme { mon: { closed, open, close }, ... }.
// Sert à afficher la bannière « Restaurant fermé » hors des heures.
// ─────────────────────────────────────────────────────────────

export interface DayHours {
  /** Jour fermé (aucune plage ouverte). */
  closed: boolean;
  /** Heure d'ouverture « HH:MM ». */
  open: string;
  /** Heure de fermeture « HH:MM ». */
  close: string;
}

export type OpeningHours = Record<string, DayHours>;

export interface WeekdayDef {
  key: string;
  label: string;
  /** Index JS getDay() : 0 = dimanche … 6 = samedi. */
  jsDay: number;
}

// Affichage à la française : lundi → dimanche.
export const WEEKDAYS: WeekdayDef[] = [
  { key: 'mon', label: 'Lundi', jsDay: 1 },
  { key: 'tue', label: 'Mardi', jsDay: 2 },
  { key: 'wed', label: 'Mercredi', jsDay: 3 },
  { key: 'thu', label: 'Jeudi', jsDay: 4 },
  { key: 'fri', label: 'Vendredi', jsDay: 5 },
  { key: 'sat', label: 'Samedi', jsDay: 6 },
  { key: 'sun', label: 'Dimanche', jsDay: 0 },
];

export const DEFAULT_DAY_HOURS: DayHours = { closed: false, open: '09:00', close: '22:00' };

/** Convertit un index getDay() vers la clé du jour (mon…sun). */
function keyFromJsDay(jsDay: number): string {
  const found = WEEKDAYS.find((d) => d.jsDay === jsDay);
  return found ? found.key : 'mon';
}

/** « HH:MM » → minutes depuis minuit (ou null si invalide). */
function toMinutes(value: string | undefined | null): number | null {
  if (!value || typeof value !== 'string') return null;
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) return null;
  return h * 60 + m;
}

/** Normalise une config brute lue en base (remplit tous les jours). */
export function normalizeOpeningHours(raw: unknown): OpeningHours {
  const result: OpeningHours = {};
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;

  WEEKDAYS.forEach((day) => {
    const entry = source ? (source[day.key] as Partial<DayHours> | undefined) : undefined;
    const open = typeof entry?.open === 'string' ? entry.open : DEFAULT_DAY_HOURS.open;
    const close = typeof entry?.close === 'string' ? entry.close : DEFAULT_DAY_HOURS.close;
    result[day.key] = {
      closed: Boolean(entry?.closed),
      open,
      close,
    };
  });

  return result;
}

/** La config contient-elle de vrais horaires (au moins un champ) ? */
export function hasOpeningHours(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  return Object.keys(raw as Record<string, unknown>).length > 0;
}

/** Un jour donné est-il ouvert à l'instant `now` ? */
function isDayOpen(day: DayHours | undefined, minutesNow: number): boolean {
  if (!day || day.closed) return false;
  const open = toMinutes(day.open);
  const close = toMinutes(day.close);
  if (open == null || close == null) return false;
  if (close > open) return minutesNow >= open && minutesNow < close;
  // Plage à cheval sur minuit (ex. 18:00 → 02:00)
  return minutesNow >= open || minutesNow < close;
}

export interface OpenState {
  /** Le restaurant est-il ouvert maintenant ? */
  open: boolean;
  /** Libellé des horaires du jour (ex. « 09:00 – 22:00 » ou « Fermé »). */
  todayLabel: string;
}

/**
 * Le restaurant est-il ouvert à l'instant donné ?
 * Si aucune config n'est fournie, on considère toujours ouvert
 * (pas de bannière « fermé »).
 */
export function getOpenState(raw: unknown, date: Date = new Date()): OpenState {
  if (!hasOpeningHours(raw)) return { open: true, todayLabel: '' };

  const hours = normalizeOpeningHours(raw);
  const minutesNow = date.getHours() * 60 + date.getMinutes();
  const jsDay = date.getDay();
  const todayKey = keyFromJsDay(jsDay);
  const today = hours[todayKey];

  let open = isDayOpen(today, minutesNow);

  // Plage overnight : si la veille ouvre tard (ex. 18:00 → 02:00) et qu'on
  // est après minuit, le restaurant peut encore être ouvert.
  if (!open && minutesNow < 12 * 60) {
    const prevKey = keyFromJsDay((jsDay + 6) % 7);
    const prev = hours[prevKey];
    const prevOpen = toMinutes(prev?.open);
    const prevClose = toMinutes(prev?.close);
    if (prev && !prev.closed && prevOpen != null && prevClose != null && prevClose <= prevOpen) {
      if (minutesNow < prevClose) open = true;
    }
  }

  const todayLabel = !today || today.closed || toMinutes(today.open) == null || toMinutes(today.close) == null
    ? 'Fermé'
    : `${today.open} – ${today.close}`;

  return { open, todayLabel };
}